import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { TraceryDivider } from "../components/Ornament";
import { useSite } from "../context/SiteContext";

const TYPE = [
  { name: "Cormorant Garamond", use: "Titles, essays & illuminated initials", cls: "font-serif" },
  { name: "DM Sans", use: "Descriptions & correspondence", cls: "font-sans" },
  { name: "JetBrains Mono", use: "Labels, coordinates & inscriptions", cls: "font-mono" },
];

const PALETTE = [
  { name: "Obsidian", hex: "#070709", note: "the ground" },
  { name: "Soot", hex: "#0d0e12", note: "chambers & cards" },
  { name: "Bloodstone", hex: "#581313", note: "accents & rites" },
  { name: "Bone", hex: "#ede8e1", note: "the written word" },
  { name: "Ash", hex: "#78716c", note: "whispers & metadata" },
];

export default function Colophon() {
  const { site } = useSite();
  const dedication = site?.settings?.dedication;

  return (
    <div className="relative min-h-screen" data-testid="colophon-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-36 pb-24 relative z-10">
        <Link to="/" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-[#78716c] hover:text-[#ede8e1] transition-colors" data-testid="colophon-back-link">
          <ArrowLeft size={14} /> The Vault
        </Link>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="mt-8">
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#581313] mb-4">Of the making of this place</p>
          <h1 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#ede8e1]" data-testid="colophon-title">
            Colophon
          </h1>
          <p className="mt-5 text-lg font-light text-[#a8a29e] leading-relaxed">
            This vault was set by hand in the dark months of MMXXVI. Its letters, colours and ornaments
            are few and chosen to endure — nothing here is decoration without office.
          </p>
          {dedication && (
            <p className="mt-8 font-serif italic text-xl text-[#ede8e1] border-l-2 border-[#581313] pl-6" data-testid="colophon-dedication">
              {dedication}
            </p>
          )}
        </motion.div>

        <motion.section initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="mt-16" data-testid="colophon-type">
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#581313] mb-6">The Type</p>
          <div className="border-t border-[#232530]">
            {TYPE.map((t) => (
              <div key={t.name} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-8 py-5 border-b border-[#232530]">
                <span className={`${t.cls} text-2xl text-[#ede8e1] sm:w-64 shrink-0`}>{t.name}</span>
                <span className="font-light text-[#a8a29e]">{t.use}</span>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="mt-16" data-testid="colophon-palette">
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#581313] mb-6">The Pigments</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {PALETTE.map((p) => (
              <div key={p.name} className="border border-[#232530] bg-[#0d0e12]" data-testid={`swatch-${p.name.toLowerCase()}`}>
                <div className="h-20" style={{ backgroundColor: p.hex }} />
                <div className="p-3">
                  <p className="font-serif text-[#ede8e1]">{p.name}</p>
                  <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#78716c]">{p.hex}</p>
                  <p className="mt-1 text-xs font-light text-[#a8a29e]">{p.note}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="mt-16" data-testid="colophon-making">
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#581313] mb-6">The Making</p>
          <div className="prose-gothic">
            <p>
              The monogram is the keeper's own. The grain is film, the arches are load-bearing, and the
              deep red is mixed once — RGB 88, 19, 19 — and used sparingly, as pigment was when it had
              to be ground by hand.
            </p>
            <p>
              Built of React and FastAPI upon a stone of MongoDB. Set, bound and sealed in the Vault.
            </p>
          </div>
        </motion.section>
        <div className="mt-20"><TraceryDivider /></div>
        <p className="mt-10 text-center font-mono text-[10px] tracking-[0.3em] uppercase text-[#57534e]">
          Explicit benedictio — MMXXVI
        </p>
      </main>
      <Footer />
    </div>
  );
}
